import { Avatar, Box, SxProps, Typography } from "@mui/material";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import ComercialDrawer from "../../../../../components/pet-beauty/CormercialDrawer";
import ComercialInterfaceHeader from "../../../../../components/comercial/ComercialInterfaceHeader";
import { IEmployee } from "../../../../../services/api/Employee/types";
import { DocType } from "../../../../../types/types";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import { sharedStyles } from "../../../../../styles/shared";
import { getLocalStorageItem } from "../../../../../hooks/localStorageHooks";
import { LeftMarginBackButton } from "../../../../../components/general/buttons/BackPageButton";
import ContactIcon from "../../../../../components/general/ContactIcon";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

const timeToMinutes = (time: string) => {
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
};

const minutesToTime = (minutes: number) => {
    const h = Math.floor(minutes / 60).toString().padStart(2, "0");
    const m = (minutes % 60).toString().padStart(2, "0");
    return `${h}:${m}`;
};

const detectInterval = (times: string[]) => {
    if (!times || times.length === 0) return 60;
    if (times.some((t) => t.endsWith(":15") || t.endsWith(":45"))) return 15;
    if (times.some((t) => t.endsWith(":30"))) return 30;
    return 60;
};

const formatDaySchedule = (times: string[], t: TFunction) => {
    if (!times || times.length === 0) return t("employeesPages.view.dayOff"); // Tradução Folga

    const sortedTimes = [...times].sort((a, b) => timeToMinutes(a) - timeToMinutes(b));
    const interval = detectInterval(sortedTimes);

    const ranges: string[] = [];
    let start = sortedTimes[0];
    let prev = sortedTimes[0];

    for (let i = 1; i < sortedTimes.length; i++) {
        const current = sortedTimes[i];
        const diff = timeToMinutes(current) - timeToMinutes(prev);

        if (diff > interval) {
            const endMinutes = timeToMinutes(prev) + interval;
            ranges.push(`${start} - ${minutesToTime(endMinutes)}`);
            start = current;
        }
        prev = current;
    }

    const lastEndMinutes = timeToMinutes(prev) + interval;
    ranges.push(`${start} - ${minutesToTime(lastEndMinutes)}`);

    return ranges.join("  |  ");
};

const EmployeeView = () => {
  const { t } = useTranslation();
  const selectedEmployee: IEmployee = getLocalStorageItem("selectedEmployee");

const renderEmployeeData = (
    <>
      <ComercialInterfaceHeader title={t("employeesPages.view.dataTitle")} />
      <Box sx={sharedStyles.verticalContent}>
        <Box
          sx={{
            ...sharedStyles.verticalContent,
            gap: "10px",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <Avatar
            alt={t("employeesPages.view.imageAlt")}
            src={selectedEmployee.User.imageOriginal}
            sx={styles.avatar}
          />
          <Typography fontWeight="bold" variant="h6">
            {selectedEmployee.User.name}
          </Typography>
          <Typography textAlign="justify">
            {selectedEmployee.User.docType === DocType.CPF
              ? `${selectedEmployee.User.docNumber} (CPF)`
              : `${selectedEmployee.User.docNumber} (Passaporte)`}
          </Typography>
        </Box>

        <Box sx={styles.singleContact}>
          <ContactIcon
            icon={<FmdGoodOutlinedIcon sx={{ color: "primary.main" }} />}
          />
          <Typography textAlign="justify">{`${
            selectedEmployee.User.logradouro
          }, ${selectedEmployee.User.number || "s/n"} - ${
            selectedEmployee.User.district
          }, ${selectedEmployee.User.city} - ${selectedEmployee.User.state}, ${
            selectedEmployee.User.CEP
          }`}</Typography>
        </Box>

        {selectedEmployee.User.phone && (
          <Box sx={styles.singleContact}>
            <ContactIcon
              icon={<LocalPhoneIcon sx={{ color: "primary.main" }} />}
            />
            <Typography textAlign="justify">
              {selectedEmployee.User.phone}
            </Typography>
          </Box>
        )}

        {selectedEmployee.User.email && (
          <Box sx={styles.singleContact}>
            <ContactIcon
              icon={<AlternateEmailIcon sx={{ color: "primary.main" }} />}
            />
            <Typography textAlign="justify">
              {selectedEmployee.User.email}
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );

    const weekSchedule = [
        { label: t("timeRange.days.monday"), times: selectedEmployee.timeMonday },
        { label: t("timeRange.days.tuesday"), times: selectedEmployee.timeTuesday },
        { label: t("timeRange.days.wednesday"), times: selectedEmployee.timeWednesday },
        { label: t("timeRange.days.thursday"), times: selectedEmployee.timeThursday },
        { label: t("timeRange.days.friday"), times: selectedEmployee.timeFriday },
        { label: t("timeRange.days.saturday"), times: selectedEmployee.timeSaturday },
        { label: t("timeRange.days.sunday"), times: selectedEmployee.timeSunday },
    ];

  const renderWorkScale = (
    <>
    <ComercialInterfaceHeader title={t("employeesPages.view.scheduleTitle")} />
    <Box sx={{ ...sharedStyles.verticalContent, gap: 1 }}>
                {weekSchedule.map((day) => (
        <Box
                        key={day.label}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            borderBottom: "1px solid #eee",
                            paddingBottom: 0.5
                        }}
                    >
                        <Typography fontWeight="bold" color="text.primary">
                            {day.label}:
                        </Typography>
                        <Typography color="text.secondary">
                            {formatDaySchedule(day.times, t)}
                        </Typography>
                    </Box>
                ))}
      </Box>
    </>
  );

  const renderAddStorePageContent = (
    <>
      <LeftMarginBackButton />

      <form style={sharedStyles.form}>
        {renderEmployeeData}
        {renderWorkScale}
      </form>
    </>
  );

  return (
    <Box sx={sharedStyles.sharedMainContainer}>
      <ComercialDrawer content={renderAddStorePageContent} />
    </Box>
  );
};

export default EmployeeView;

const styles = {
  avatar: { width: "150px", height: "150px" } as SxProps,

  singleContact: { display: "flex", alignItems: "center", gap: 1 } as SxProps,
};
